import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import './TeacherFreeTier.css';
import NotebookPanel from './NotebookPanel';

/**
 * TeacherLiteDashboard — landing dashboard for free-forever teacher tier.
 *
 * Per docs/FREE_TEACHER_TIER_SCOPE.md Day 3.
 *
 * Does NOT require school_license_id (unlike the full MorpheusInterface).
 * Shows: class info, active roster (or placeholder), mocked outcome preview,
 * referral CTA, upgrade-to-school CTA.
 */

const TeacherLiteDashboard = ({ user, onLogout }) => {
  const [roster, setRoster] = useState([]);
  const [classCode, setClassCode] = useState(null);
  const [className, setClassName] = useState('My class');
  const [classes, setClasses] = useState([]);        // multi-class (one per period)
  const [maxClasses, setMaxClasses] = useState(6);
  const [addingClass, setAddingClass] = useState(false);
  const [showAddClass, setShowAddClass] = useState(false);
  const [newClassName, setNewClassName] = useState('');
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(''), 5000); };

  const fetchClass = async () => {
    // Day 4 endpoint — graceful fallback to empty state if not yet shipped
    try {
      const response = await axios.get('/api/classroom/my-class');
      const data = response.data || {};
      setRoster(data.students || []);
      setClassCode(data.code || null);
      if (data.class_name) setClassName(data.class_name);
      setClasses(data.classes || []);
      if (data.max_classes) setMaxClasses(data.max_classes);
    } catch (err) {
      // No class created yet — show empty state
    } finally {
      setLoading(false);
      // Keep sibling panels (NotebookPanel's class picker) in sync without
      // a page reload — stale class lists mis-targeted assignments
      // (field-reported).
      window.dispatchEvent(new Event('tappy:classes-changed'));
    }
  };

  const renameClass = async (c) => {
    const name = renameValue.trim();
    if (!name || name === c.class_name) { setRenamingId(null); return; }
    try {
      await axios.patch(`/api/classroom/classes/${c.invite_id}`, { class_name: name });
      setRenamingId(null);
      fetchClass();
    } catch (err) {
      flash(err.response?.data?.error || 'Could not rename class');
    }
  };

  useEffect(() => { fetchClass(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteClass = async (c) => {
    if (!window.confirm(
      `Delete "${c.class_name}"? The class code stops working, students are removed from your roster, and its assignments stop working. Session history is kept.`
    )) return;
    try {
      await axios.delete(`/api/classroom/classes/${c.invite_id}`);
      fetchClass();
    } catch (err) {
      flash(err.response?.data?.error || 'Could not delete class');
    }
  };

  const goToShare = () => navigate('/teacher/share');
  const goToUpgrade = () => navigate('/upgrade?source=teacher_dashboard&target=school');

  // One-click class creation (no wizard, no prompt dialogs \u2014 Part 16
  // items 2 & 3). Server defaults the name; teachers rename via Add-class
  // for additional periods.
  const createFirstClass = async () => {
    setAddingClass(true);
    try {
      const r = await axios.post('/api/classroom/generate-class-code', {});
      if (r.data.success) {
        await fetchClass();
      } else {
        flash(r.data.error || 'Could not create class');
      }
    } catch (err) {
      flash(err.response?.data?.error || 'Could not create class');
    } finally { setAddingClass(false); }
  };

  const addClass = async () => {
    const name = newClassName.trim();
    if (!name) return;
    setAddingClass(true);
    try {
      const r = await axios.post('/api/classroom/generate-class-code',
        { class_name: name, new_class: true });
      if (r.data.success) {
        setNewClassName('');
        setShowAddClass(false);
        await fetchClass();
      } else {
        flash(r.data.error || 'Could not create class');
      }
    } catch (err) {
      flash(err.response?.data?.error || 'Could not create class');
    } finally { setAddingClass(false); }
  };

  return (
    <div className="teacher-lite-page">
      {/* Top bar */}
      <div className="teacher-lite-topbar">
        <div className="teacher-lite-brand">
          <img src="/images/TAPPY-logo-black.png" alt="Tappy" className="teacher-lite-logo" />
          <span className="teacher-lite-tier-badge">Free</span>
        </div>
        <div className="teacher-lite-topbar-actions">
          <span className="teacher-lite-user">{user?.name || user?.username}</span>
          <button className="teacher-btn-ghost" onClick={onLogout}>Sign out</button>
        </div>
      </div>

      <div className="teacher-lite-main">
        {/* Welcome / primary panel */}
        <section className="teacher-lite-section">
          <h1 className="teacher-lite-heading">{classes.length > 1 ? 'Your classes' : className}</h1>
          <p className="teacher-lite-subhead">
            Free-forever tier · up to {maxClasses} classes of 30 students · unlimited Mira sessions
          </p>

          {notice && <div className="teacher-step-hint">{notice}</div>}

          {classCode ? (
            <>
              {(classes.length ? classes : [{ invite_id: 0, code: classCode, class_name: className, students: roster, student_count: roster.length, max_students: 30 }]).map((c) => (
                <div key={c.invite_id} className="teacher-code-display teacher-code-inline" style={{ marginBottom: 10 }}>
                  <div>
                    {renamingId === c.invite_id ? (
                      <input
                        className="teacher-input"
                        style={{ marginBottom: 4, padding: '4px 8px', fontSize: 13, maxWidth: 220 }}
                        value={renameValue}
                        maxLength={80}
                        autoFocus
                        onChange={(e) => setRenameValue(e.target.value)}
                        onBlur={() => renameClass(c)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') renameClass(c);
                          if (e.key === 'Escape') setRenamingId(null);
                        }}
                      />
                    ) : (
                      <div
                        className="teacher-code-label"
                        title="Click to rename"
                        style={{ cursor: 'pointer' }}
                        onClick={() => { setRenamingId(c.invite_id); setRenameValue(c.class_name); }}
                      >
                        {c.class_name} <span aria-hidden="true" style={{ opacity: 0.45 }}>✎</span>
                      </div>
                    )}
                    <div className="teacher-code-value">{c.code}</div>
                  </div>
                  <span className="teacher-lite-student-stats" style={{ marginRight: 'auto', marginLeft: 12 }}>
                    {c.student_count}/{c.max_students || 30}
                  </span>
                  {/* Scannable join QR — teacher projects the dashboard,
                      students scan. Generated client-side. */}
                  <QRCodeSVG
                    value={`${window.location.origin.replace('app.', '')}/class-join?code=${c.code}`}
                    size={72}
                    fgColor="#1b3f48"
                    style={{ marginRight: 12, flexShrink: 0 }}
                  />
                  <button
                    className="teacher-btn-secondary"
                    onClick={() => navigator.clipboard?.writeText(c.code)}
                  >
                    Copy
                  </button>
                  {c.invite_id ? (
                    <button
                      className="teacher-btn-ghost"
                      title="Delete this class"
                      style={{ marginLeft: 6 }}
                      onClick={() => deleteClass(c)}
                    >
                      Delete
                    </button>
                  ) : null}
                </div>
              ))}
              {classes.length < maxClasses && !showAddClass && (
                <button className="teacher-btn-secondary" onClick={() => setShowAddClass(true)}>
                  + Add a class (another period)
                </button>
              )}
              {showAddClass && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    className="teacher-input"
                    style={{ maxWidth: 280, marginBottom: 0 }}
                    placeholder="e.g. Period 3 — AP Gov"
                    value={newClassName}
                    maxLength={80}
                    autoFocus
                    onChange={(e) => setNewClassName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') addClass(); if (e.key === 'Escape') setShowAddClass(false); }}
                  />
                  <button className="teacher-btn-primary" onClick={addClass}
                          disabled={addingClass || !newClassName.trim()}>
                    {addingClass ? 'Creating…' : 'Create'}
                  </button>
                  <button className="teacher-btn-ghost" onClick={() => setShowAddClass(false)}>
                    Cancel
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="teacher-lite-cta-card">
              <p>
                Get a class code so students can join — one click, we name it
                for you (rename any time).
              </p>
              <button className="teacher-btn-primary" onClick={createFirstClass} disabled={addingClass}>
                {addingClass ? 'Creating…' : 'Create my class code →'}
              </button>
            </div>
          )}
        </section>

        {/* Class materials & assignments (NotebookLM outcome loop) */}
        <NotebookPanel />

        {/* Roster */}
        <section className="teacher-lite-section">
          <h2 className="teacher-lite-subheading">Students</h2>
          {loading ? (
            <div className="teacher-lite-empty">Loading...</div>
          ) : roster.length === 0 ? (
            <div className="teacher-lite-empty">
              <p>Your first student will appear here once they join with your class code.</p>
              <p className="teacher-lite-empty-hint">
                Students go to teachtappy.ai/class-join and enter the code + their first name.
                No email or password needed.
              </p>
            </div>
          ) : classes.length > 1 ? (
            classes.map((c) => (
              <div key={c.invite_id} style={{ marginBottom: 12 }}>
                <div className="teacher-lite-subhead" style={{ marginBottom: 6 }}>
                  {c.class_name} · {c.student_count} student{c.student_count === 1 ? '' : 's'}
                </div>
                <div className="teacher-lite-roster">
                  {c.students.map((student) => (
                    <div key={student.id} className="teacher-lite-student-row">
                      <div className="teacher-lite-student-name">{student.first_name}</div>
                      <div className="teacher-lite-student-stats">
                        {student.sessions} sessions · mastery {Math.round((student.mastery || 0) * 100)}%
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="teacher-lite-roster">
              {roster.map((student) => (
                <div key={student.id} className="teacher-lite-student-row">
                  <div className="teacher-lite-student-name">{student.first_name}</div>
                  <div className="teacher-lite-student-stats">
                    {student.sessions} sessions · mastery {Math.round((student.mastery || 0) * 100)}%
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Outcome preview — faded mock until real data exists */}
        <section className="teacher-lite-section teacher-lite-preview">
          <h2 className="teacher-lite-subheading">What you'll see after their first session</h2>
          <div className="teacher-lite-preview-card">
            <div className="teacher-lite-preview-row">
              <span>Mastery by topic</span>
              <span className="teacher-lite-preview-muted">Fractions · Photosynthesis · Pythagorean theorem</span>
            </div>
            <div className="teacher-lite-preview-row">
              <span>Misconceptions to address</span>
              <span className="teacher-lite-preview-muted">Teacher dashboard surfaces patterns across students</span>
            </div>
            <div className="teacher-lite-preview-row">
              <span>Minutes saved</span>
              <span className="teacher-lite-preview-muted">Estimated re-teach time spared by Mira</span>
            </div>
          </div>
          <p className="teacher-lite-preview-hint">
            This is a preview. Real data appears here as soon as a student completes a session.
          </p>
        </section>

        {/* Referral + upgrade */}
        <section className="teacher-lite-section teacher-lite-cta-grid">
          <div className="teacher-lite-cta-card">
            <h3 className="teacher-lite-cta-title">Share with a colleague</h3>
            <p>Teachers who refer get early access to new Tappy features. Free tier never expires.</p>
            <button className="teacher-btn-secondary" onClick={goToShare}>
              Invite a colleague
            </button>
          </div>

          <div className="teacher-lite-cta-card teacher-lite-cta-upgrade">
            <h3 className="teacher-lite-cta-title">Ready for your whole school?</h3>
            <p>
              Unlock Canvas / Schoology / Classroom integration, gradebook passback,
              roster sync, and unlimited students. Our team will work with your district on a DPA.
            </p>
            <button className="teacher-btn-primary" onClick={goToUpgrade}>
              See school plans
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default TeacherLiteDashboard;
